import { useContext, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TabsContent } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/utils/formatters';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CloudDownload, Clock, RefreshCw } from 'lucide-react';
import { ApuracaoDetalhadaContext } from '@/contexts/ApuracaoDetalhadaContext';
import {
  useConsultarSituacaoApuracaoAssistida,
  useSolicitarApuracaoAssistida,
  useUltimaApuracaoAssistida,
} from '@/services/ApuracaoCBS/hooks/useApuracaoAssistidaCBS';
import type { ApuracaoAssistidaResponse } from '@/services/ApuracaoCBS/types';

function formatarMomento(valor: string | null | undefined) {
  if (!valor) return null;

  const data = new Date(valor);

  return Number.isNaN(data.getTime())
    ? valor
    : data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatarCompetencia(pa: string | null) {
  if (!pa) return '—';

  const casa = /^(\d{4})-?(\d{2})$/.exec(pa.trim());

  return casa ? `${casa[2]}/${casa[1]}` : pa;
}

function Aviso({
  titulo,
  mensagem,
  detalhe,
  acao,
  erro = false,
}: {
  titulo: string;
  mensagem: string;
  detalhe?: string;
  acao?: React.ReactNode;
  erro?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <Card
        className={erro ? 'border-destructive/50' : undefined}
        role={erro ? 'alert' : undefined}
      >
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle
              className={`h-5 w-5 mt-0.5 ${erro ? 'text-destructive' : 'text-muted-foreground'}`}
            />
            <div className="space-y-2">
              <p className={`font-semibold ${erro ? 'text-destructive' : ''}`}>{titulo}</p>
              <p className="text-sm text-muted-foreground">{mensagem}</p>
              {detalhe && <p className="text-xs text-muted-foreground">{detalhe}</p>}
              {acao}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function motivoDaFalha(
  chamadaFalhou: boolean,
  erro: Error | null,
  resposta: ApuracaoAssistidaResponse | undefined
): string | null {
  if (chamadaFalhou) return erro?.message || 'A consulta não respondeu.';
  if (resposta?.status.resultado === false) {
    return resposta.status.mensagem || 'A consulta foi recusada.';
  }
  return null;
}

type EstadoDaAba = 'sem-empresa' | 'carregando' | 'sem-conexao' | 'erro' | 'sem-dado' | 'conteudo';

export function ApuracaoCbsRtcTab() {
  const ctx = useContext(ApuracaoDetalhadaContext);
  if (!ctx) {
    throw new Error('ApuracaoCbsRtcTab deve ser usado dentro de <ApuracaoDetalhadaProvider>');
  }

  const { buscaAplicada, filtrosAplicados } = ctx;
  const cnpjContribuinte = filtrosAplicados.empresaId ?? '';

  const [tiqueteEmAberto, setTiqueteEmAberto] = useState<string | null>(null);

  const {
    data: resposta,
    error,
    isError,
    isFetching,
    isPaused,
    refetch,
  } = useUltimaApuracaoAssistida(cnpjContribuinte, buscaAplicada);

  const solicitar = useSolicitarApuracaoAssistida();
  const situacao = useConsultarSituacaoApuracaoAssistida();

  const falha = motivoDaFalha(isError, error, resposta);
  /* Só uma resposta de sucesso diz o que a Receita já mandou. No React Query 5, o refetch que falha
     deixa a consulta em erro e mantém o `data` anterior: a apuração que já estava na tela fica nela. */
  const respostaOk = resposta?.status?.resultado === true ? resposta : undefined;
  const periodos = respostaOk?.data ?? [];
  const primeiro = periodos[0];

  /* A ordem decide o que a tela afirma. Sem apuração na tela, o erro vem antes de "nenhuma
     apuração", e "nenhuma apuração" só aparece com uma resposta de sucesso. Sem rede, a consulta
     fica pausada e não respondeu nada. */
  const estado: EstadoDaAba =
    !buscaAplicada || !cnpjContribuinte
      ? 'sem-empresa'
      : periodos.length > 0
        ? 'conteudo'
        : falha !== null
          ? 'erro'
          : respostaOk
            ? 'sem-dado'
            : isPaused
              ? 'sem-conexao'
              : 'carregando';

  const geradoEm = formatarMomento(primeiro?.GeradoEm);
  const recebidoEm = formatarMomento(primeiro?.DataRecebimento);

  const handleSolicitar = () =>
    solicitar.mutate(cnpjContribuinte, {
      onSuccess: resposta => {
        if (resposta.status.resultado && resposta.data?.TiqueteSolicitacao) {
          setTiqueteEmAberto(resposta.data.TiqueteSolicitacao);
        }
      },
    });

  const botaoSolicitar = (
    <Button
      size="sm"
      variant="outline"
      className="gap-2"
      disabled={solicitar.isPending || !cnpjContribuinte}
      onClick={handleSolicitar}
    >
      <CloudDownload className={`h-3.5 w-3.5 ${solicitar.isPending ? 'animate-pulse' : ''}`} />
      {periodos.length > 0 ? 'Solicitar atualização' : 'Solicitar à Receita'}
    </Button>
  );

  const botaoTentarDeNovo = (
    <Button
      size="sm"
      variant="outline"
      className="gap-2"
      disabled={isFetching}
      onClick={() => refetch()}
    >
      <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
      Tentar de novo
    </Button>
  );

  return (
    <TabsContent value="apuracao-cbs-rtc" className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="bg-primary text-primary-foreground px-6 py-3 rounded-lg"
      >
        <h2 className="text-xl font-bold">Apuração CBS (Receita)</h2>
      </motion.div>

      {tiqueteEmAberto && (
        <Card className="border-amber-300 bg-amber-50/40">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-700 mt-0.5" />
              <div className="space-y-2">
                <p className="font-semibold text-amber-900">Solicitação em processamento</p>
                <p className="text-sm text-amber-900/80">
                  A Receita protocolou o pedido e leva até 4 horas para disponibilizar o resultado.
                  Assim que ficar pronto, ele chega sozinho — não é preciso manter esta tela aberta.
                </p>
                <p className="text-xs font-mono text-amber-900/70">Tíquete {tiqueteEmAberto}</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  disabled={situacao.isPending}
                  onClick={() =>
                    situacao.mutate({ cnpj: cnpjContribuinte, tiquete: tiqueteEmAberto })
                  }
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${situacao.isPending ? 'animate-spin' : ''}`}
                  />
                  Consultar agora
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <AnimatePresence mode="wait" initial={false}>
        {estado === 'sem-empresa' ? (
          <Aviso
            key="cbs-sem-empresa"
            titulo="Selecione a empresa"
            mensagem="Escolha a empresa no header e clique em Buscar para ver a última apuração que a Receita nos enviou."
          />
        ) : estado === 'carregando' ? (
          <motion.div
            key="cbs-carregando"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <Card>
              <CardContent className="pt-6 space-y-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          </motion.div>
        ) : estado === 'sem-conexao' ? (
          <Aviso
            key="cbs-sem-conexao"
            titulo="Sem conexão com a internet"
            mensagem="A consulta da última apuração sai assim que a conexão voltar. Até lá, esta tela não sabe se já chegou alguma apuração."
          />
        ) : estado === 'erro' ? (
          <Aviso
            key="cbs-erro"
            erro
            titulo="Não foi possível consultar a apuração da Receita"
            mensagem="A consulta deu erro, então esta tela não sabe se já chegou alguma apuração. Tente de novo; se o erro continuar, acione o suporte."
            detalhe={`Detalhe: ${falha}`}
            acao={botaoTentarDeNovo}
          />
        ) : estado === 'sem-dado' ? (
          <Aviso
            key="cbs-sem-dado"
            titulo="Nenhuma apuração recebida ainda"
            mensagem="A consulta de débitos da CBS não é imediata: a Receita protocola o pedido, processa em até 4 horas e envia o resultado depois. Solicite abaixo — o número aparece aqui quando chegar."
            acao={botaoSolicitar}
          />
        ) : (
          <motion.div
            key="cbs-conteudo"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="space-y-6"
          >
            {falha !== null && (
              <Aviso
                erro
                titulo="Não foi possível atualizar a apuração da Receita"
                mensagem="A última consulta deu erro. A apuração abaixo é a da consulta anterior e pode estar desatualizada. Tente de novo; se o erro continuar, acione o suporte."
                detalhe={`Detalhe: ${falha}`}
                acao={botaoTentarDeNovo}
              />
            )}

            <Card className="border-primary">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="space-y-1">
                    <Badge variant="outline" className="gap-1">
                      Dados da Receita Federal
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      {geradoEm
                        ? `Apurados pela Receita em ${geradoEm}`
                        : 'Data de apuração não informada'}
                      {recebidoEm ? ` · recebidos em ${recebidoEm}` : ''}
                    </p>
                  </div>
                  {botaoSolicitar}
                </div>
              </CardHeader>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Competência</TableHead>
                      <TableHead className="text-right">Apurado</TableHead>
                      <TableHead className="text-right">Saldo devedor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {periodos.map(periodo => (
                      <TableRow key={`${periodo.TiqueteSolicitacao}-${periodo.Pa}`}>
                        <TableCell className="font-medium">
                          {formatarCompetencia(periodo.Pa)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(periodo.Apurado)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(periodo.SaldoDevedor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
              Esta aba espelha o que a Receita Federal apurou, sem cruzamento com a escrituração.
              Divergências entre os dois lados aparecem no Resumo Geral.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </TabsContent>
  );
}
